import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { manageAdminCustomerQueryOptions } from "../../../queryOptions/ManageUsersOptions";
import { updateAdminCustomer } from "../../../apis/userApi";
import { AdminCustomerMembership } from "../../../schemas/user";

interface UserDetailsDialogProps {
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

const formatMembershipType = (type: string) =>
  type
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatCurrency = (amount: number) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "$0";
  return `$${value}`;
};

const formatSignedInDate = (value?: string | Date) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const SIGNUP_PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%*?&])[A-Za-z\d@#$!%*?&]{8,}$/;

const getSignupPasswordError = (value: string): string | undefined => {
  if (!value) return undefined;
  if (value.length < 8) {
    return "Password must be at least 8 characters";
  }
  if (!SIGNUP_PASSWORD_REGEX.test(value)) {
    return "Password must include uppercase, lowercase, number, and special character";
  }
  return undefined;
};

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const UserDetailsDialog = ({
  open,
  userId,
  onClose,
  onUpdated,
}: UserDetailsDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAdmin = localStorage.getItem("userType") === "admin";
  const [tabValue, setTabValue] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [password, setPassword] = useState("");

  const { data: customer, isLoading, isFetching, refetch } = useQuery({
    ...manageAdminCustomerQueryOptions(open ? userId : null),
  });

  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setShowNewPassword(false);
      setPassword("");
      return;
    }
    if (customer) {
      setFirstName(customer.firstName || "");
      setLastName(customer.lastName || "");
      setEmail(customer.email || "");
      setPhoneNumber(customer.phoneNumber || "");
      setAddress(customer.address || "");
    }
  }, [open, customer]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!userId) return;

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phoneNumber.trim()) {
      toast.error("First name, last name, email, and phone number are required");
      return;
    }

    if (isAdmin && password) {
      const passwordError = getSignupPasswordError(password);
      if (passwordError) {
        toast.error(passwordError);
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload: Parameters<typeof updateAdminCustomer>[1] = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        address: address.trim(),
      };
      if (isAdmin && password) {
        payload.password = password;
      }
      await updateAdminCustomer(userId, payload);
      toast.success("User updated successfully");
      setPassword("");
      setShowNewPassword(false);
      setTabValue(0);
      await refetch();
      onUpdated();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update user. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const membershipDetail =
    customer && customer.isMember && customer.memberships.length > 0
      ? customer.memberships
          .map((membership: AdminCustomerMembership) =>
            membership.type
              ? `${membership.name} (${formatMembershipType(membership.type)})`
              : membership.name
          )
          .join(", ")
      : undefined;
  const enrollmentDetail =
    customer && customer.isEnrolled && customer.programs.length > 0
      ? customer.programs.join(", ")
      : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle component="div" sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            User Details
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close user details">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 2, sm: 3 } }}>
        <Tabs
          value={tabValue}
          onChange={(_event, value) => setTabValue(value)}
          aria-label="User details tabs"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="View" />
          <Tab icon={<EditIcon />} iconPosition="start" label="Edit" />
        </Tabs>
      </Box>
      <DialogContent sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        {isLoading || (isFetching && !customer) ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !customer ? (
          <Typography color="text.secondary">User details could not be loaded.</Typography>
        ) : (
          <>
            <Box role="tabpanel" hidden={tabValue !== 0}>
              {tabValue === 0 && (
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="First Name" value={customer.firstName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Last Name" value={customer.lastName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Email Address" value={customer.email} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Phone Number" value={customer.phoneNumber} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Address" value={customer.address} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyField
                      label="Signed In Date"
                      value={formatSignedInDate(customer.createdAt)}
                    />
                  </Grid>
                  {isAdmin && (
                    <Grid item xs={12} sm={6}>
                      <ReadOnlyField
                        label="Total Bookings"
                        value={String(customer.totalBookings ?? 0)}
                      />
                    </Grid>
                  )}
                  {isAdmin && (
                    <Grid item xs={12} sm={6}>
                      <ReadOnlyField
                        label="Total Revenue"
                        value={formatCurrency(customer.totalRevenue ?? 0)}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, height: "100%", bgcolor: "grey.50" }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.75 }}
                      >
                        Member
                      </Typography>
                      <Chip
                        label={customer.isMember ? "Active Member" : "Not a Member"}
                        color={customer.isMember ? "success" : "default"}
                        size="small"
                      />
                      {membershipDetail && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {membershipDetail}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, height: "100%", bgcolor: "grey.50" }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.75 }}
                      >
                        Program Enrollment
                      </Typography>
                       <Chip
                         label={customer.isEnrolled ? "Enrolled" : "No Enrollment"}
                         color={customer.isEnrolled ? "info" : "default"}
                         size="small"
                       />
                      {enrollmentDetail && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {enrollmentDetail}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
             <Box role="tabpanel" hidden={tabValue !== 1}>
              {tabValue === 1 && (
                <form onSubmit={handleSave} id="admin-customer-edit-form">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="First Name"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Last Name"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        label="Email Address"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Phone Number"
                        value={phoneNumber}
                        onChange={(event) => setPhoneNumber(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={address}
                        onChange={(event) => setAddress(event.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                    {isAdmin && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="New Password"
                          type={showNewPassword ? "text" : "password"}
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          error={!!getSignupPasswordError(password)}
                          helperText={
                            getSignupPasswordError(password) ||
                            "Leave blank to keep the current password."
                          }
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    type="button"
                                    aria-label={
                                      showNewPassword
                                        ? "Hide new password"
                                        : "Show new password"
                                    }
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    onClick={() =>
                                      setShowNewPassword((prev) => !prev)
                                    }
                                    edge="end"
                                  >
                                    {showNewPassword ? (
                                      <VisibilityOffIcon />
                                    ) : (
                                      <VisibilityIcon />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </form>
              )}
            </Box> 
          </>
        )}
      </DialogContent>
      {tabValue === 1 && (
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="admin-customer-edit-form"
            variant="contained"
            disabled={isSaving || !customer}
            startIcon={
              isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
            }
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UserDetailsDialog;
